/* ScansUploadedNudge — iter97.88 (Phase A4.1)
 *
 * Doctor's dashboard nudge that lists every case in `scan_visit_completed`
 * status — i.e. the technician has captured scans + photos on-site and
 * the case is now waiting for the doctor to author the treatment plan.
 *
 * Each row deep-links into the case detail (which surfaces the
 * ScanVisitCaseView's "Continue Case Wizard" CTA) so the doctor's path
 * from "see the nudge" → "resume wizard" is two clicks max.
 *
 * The component is intentionally self-hiding when the list is empty so
 * the doctor's home stays calm on quiet days.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Camera, User } from "@phosphor-icons/react";

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
    catch { return ""; }
}

export default function ScansUploadedNudge({ cases }) {
    const items = useMemo(() => (
        (cases || [])
            .filter((c) => c.status === "scan_visit_completed")
            .sort((a, b) => {
                const ta = new Date(a.scan_visit_completed_at || a.updated_at || 0).getTime();
                const tb = new Date(b.scan_visit_completed_at || b.updated_at || 0).getTime();
                return tb - ta;
            })
    ), [cases]);

    if (items.length === 0) return null;

    return (
        <div
            className="rounded-2xl border border-primary/25 bg-primary/5 overflow-hidden"
            data-testid="scans-uploaded-nudge"
        >
            <div className="px-5 py-3.5 border-b border-primary/15 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center text-primary shrink-0">
                    <Camera size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-base font-bold leading-tight">
                        Scans &amp; photos uploaded — ready to continue
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {items.length} case{items.length === 1 ? "" : "s"} waiting for your treatment plan.
                    </p>
                </div>
            </div>
            <ul className="divide-y divide-primary/15">
                {items.slice(0, 5).map((c) => {
                    // iter98.68 — Anonymize the technician identity to a
                    // generic "Center of Excellence Team" label per user
                    // request. The doctor doesn't need (and shouldn't
                    // see) the individual technician's name. The real
                    // identity is still preserved server-side on
                    // `c.scan_visit_technician_id` for audit + admin
                    // surfaces.
                    const tech = "Center of Excellence Team";
                    const fileCount = c.scan_visit_file_count || 0;
                    const when = c.scan_visit_completed_at;
                    return (
                        <li key={c.id}>
                            <Link
                                to={`/doctor/cases/${c.id}`}
                                className="flex items-center gap-3 px-5 py-3 hover:bg-primary/10 transition-colors"
                                data-testid={`scans-uploaded-nudge-row-${c.id}`}
                            >
                                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                    <CheckCircle size={14} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{c.patient_name || "Patient"}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">
                                        {fileCount > 0 ? `${fileCount} file${fileCount === 1 ? "" : "s"} uploaded by ` : "Uploaded by "}
                                        <span className="font-medium text-foreground/80">{tech}</span>
                                        {when && <> · {fmtDate(when)}</>}
                                    </div>
                                </div>
                                <div className="text-xs font-semibold text-primary inline-flex items-center gap-1 shrink-0">
                                    Continue case
                                    <ArrowRight size={12} weight="bold" />
                                </div>
                            </Link>
                        </li>
                    );
                })}
                {items.length > 5 && (
                    <li>
                        <Link
                            to="/doctor/cases?stage=scans-uploaded"
                            className="block px-5 py-2.5 text-xs font-semibold text-primary hover:underline"
                            data-testid="scans-uploaded-nudge-see-all"
                        >
                            See all {items.length} →
                        </Link>
                    </li>
                )}
            </ul>
        </div>
    );
}
